// Re:Vault - settings store. localStorage-backed, schema-defaulted, with live
// cross-document sync (app tab <-> settings tab) via BroadcastChannel.

import { SETTINGS_KEY, SETTINGS_VERSION, DEFAULTS, migrateSettings } from "./settings-schema.js";
import { metric } from "./metric.js";

// Which settings someone changes (the name only, once per page), for the private counter.
const changed = new Set();

const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("revault") : null;
const subs = new Set();
let cache = null;

// Keys that must never be copied from an untrusted stored/imported blob: a JSON
// payload with an own "__proto__" (or constructor/prototype) key would poison the
// cache object's prototype when merged via [[Set]] (Object.assign / all[k]=…).
const UNSAFE_KEY = (k) => k === "__proto__" || k === "constructor" || k === "prototype";

function load() {
	let stored = {};
	try { stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") || {}; } catch (e) {}
	if (typeof stored !== "object" || Array.isArray(stored)) stored = {};
	// Every document runs this on its first read, so the app tab, the Settings tab and a restored
	// backup all migrate the same way. Written back at once when a stored blob actually changed.
	const hadStored = Object.keys(stored).length > 0;
	const migrated = hadStored && migrateSettings(stored);
	const out = Object.assign({}, DEFAULTS);
	for (const k in stored) {
		if (UNSAFE_KEY(k) || !Object.prototype.hasOwnProperty.call(stored, k)) continue;
		out[k] = stored[k];
	}
	out._v = SETTINGS_VERSION;
	if (migrated) { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(out)); } catch (e) {} }
	return out;
}

export function getAll() {
	if (!cache) cache = load();
	return cache;
}
export function get(key) { return getAll()[key]; }

function persist() {
	try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(cache)); } catch (e) {}
}

export function set(key, value) {
	const all = getAll();
	if (all[key] === value) return;
	all[key] = value;
	persist();
	if (!changed.has(key) && Object.prototype.hasOwnProperty.call(DEFAULTS, key)) { changed.add(key); metric("setting:" + key); }
	// "Show ads" both ways, so the share of people turning ads off can be watched during a trial.
	if (key === "adsEnabled") metric(value === false ? "ads:off" : "ads:on");
	if (channel) channel.postMessage({ type: "set", key, value });
	notify({ keys: [key] });
}

export function setMany(patch) {
	const all = getAll();
	const keys = [];
	for (const k in patch) { if (UNSAFE_KEY(k) || !Object.prototype.hasOwnProperty.call(patch, k)) continue; if (all[k] !== patch[k]) { all[k] = patch[k]; keys.push(k); } }
	if (!keys.length) return;
	persist();
	if (channel) channel.postMessage({ type: "patch", patch });
	notify({ keys });
}

export function resetAll() {
	cache = Object.assign({}, DEFAULTS);
	persist();
	if (channel) channel.postMessage({ type: "reset" });
	notify({ reset: true });
}

export function exportJSON() { return JSON.stringify(getAll(), null, 2); }
export function importJSON(text) {
	try {
		const obj = JSON.parse(text);
		// An exported file from an older version migrates exactly like stored settings.
		if (obj && typeof obj === "object") { migrateSettings(obj); setMany(obj); return true; }
	} catch (e) {}
	return false;
}

// subscribe(fn) -> unsubscribe. fn(settings, detail) on any change (local or remote).
export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
function notify(detail) { const s = getAll(); subs.forEach((fn) => { try { fn(s, detail); } catch (e) {} }); }

// Remote changes from the other document. The message itself is never trusted (any page on this
// origin can post to the channel): it only means "re-read localStorage", and what is read there is
// re-checked where it is used (URL settings: settings-schema.js safeLinuxWispUrl, app/cloak.js).
if (channel) {
	channel.onmessage = () => { cache = null; notify({ remote: true }); };
}
if (typeof window !== "undefined") {
	window.addEventListener("storage", (e) => {
		if (e.key === SETTINGS_KEY) { cache = null; notify({ remote: true }); }
	});
}
